import * as React from 'react';
import type { GuestVoucherDto } from './GuestVoucherCard';
import { useFlowProgress, type StepStatus } from './useFlowProgress';
import { isAuthFailureResponse, logAuthFailure, SESSION_EXPIRED_MESSAGE } from './authFailure';

interface Coach {
    personId: string;
    contactId: string;
    firstName: string;
    lastName: string;
    coachLevel: string;
}

interface Location {
    locationId: string;
    displayName: string;
}

export interface VoucherService {
    serviceId: string;
    displayName: string;
    eligibleVoucherTypes: string[];
    coaches: Coach[];
    locations: Location[];
}

export interface ConvertFlowProps {
    voucher: GuestVoucherDto;
    service: VoucherService;
    onDone: () => void;
    onBack: () => void;
}

function StepIcon({ status }: { status: StepStatus }) {
    switch (status) {
        case 'pending':
            return <span style={{ width: '20px', display: 'inline-block', textAlign: 'center', color: '#6c757d' }}>&#9675;</span>;
        case 'running':
            return (
                <span style={{ width: '20px', display: 'inline-block', textAlign: 'center' }}>
                    <span className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '14px', height: '14px' }} />
                </span>
            );
        case 'completed':
            return <span style={{ width: '20px', display: 'inline-block', textAlign: 'center', color: '#059669' }}>&#10003;</span>;
        case 'failed':
            return <span style={{ width: '20px', display: 'inline-block', textAlign: 'center', color: '#dc3545' }}>&#10007;</span>;
    }
}

function todayDateString(): string {
    return new Date().toISOString().slice(0, 10);
}

export const ConvertFlow: React.FC<ConvertFlowProps> = ({ voucher, service, onDone, onBack }) => {
    const [selectedCoachId, setSelectedCoachId] = React.useState(
        () => service.coaches.length > 0 ? service.coaches[0].personId : ''
    );
    const [selectedLocationId, setSelectedLocationId] = React.useState(
        () => service.locations.length > 0 ? service.locations[0].locationId : ''
    );
    const [bookingDate, setBookingDate] = React.useState(todayDateString());
    const [bookingTime, setBookingTime] = React.useState('09:00');
    const [coachSearch, setCoachSearch] = React.useState('');
    const [locationSearch, setLocationSearch] = React.useState('');
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    const flow = useFlowProgress();
    const coaches = service.coaches;
    const locations = service.locations;

    // If voucher already has an active flow, attach immediately
    React.useEffect(() => {
        if (voucher.activeFlowId) {
            flow.startFlow(voucher.activeFlowId);
        }
    }, [voucher.activeFlowId]);

    const filteredCoaches = React.useMemo(() => {
        if (!coachSearch.trim()) return coaches;
        const term = coachSearch.toLowerCase();
        return coaches.filter(c =>
            c.firstName.toLowerCase().includes(term) ||
            c.lastName.toLowerCase().includes(term) ||
            (c.coachLevel && c.coachLevel.toLowerCase().includes(term))
        );
    }, [coaches, coachSearch]);

    const filteredLocations = React.useMemo(() => {
        if (!locationSearch.trim()) return locations;
        const term = locationSearch.toLowerCase();
        return locations.filter(l => l.displayName.toLowerCase().includes(term));
    }, [locations, locationSearch]);

    const selectedCoach = coaches.find(c => c.personId === selectedCoachId);
    const selectedLocation = locations.find(l => l.locationId === selectedLocationId);

    const handleConvert = async () => {
        if (!selectedCoachId || !selectedLocationId) return;
        setSubmitError(null);

        const bookingStart = new Date(`${bookingDate}T${bookingTime}:00`).toISOString();

        try {
            const response = await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/convert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    serviceId: service.serviceId,
                    coachPersonId: selectedCoachId,
                    bookingStart,
                    locationId: selectedLocationId,
                }),
            });

            if (isAuthFailureResponse(response)) {
                logAuthFailure('ConvertFlow.handleConvert', response);
                setSubmitError(SESSION_EXPIRED_MESSAGE);
                return;
            }

            if (!response.ok) {
                try {
                    const err = await response.json();
                    setSubmitError(err.error || `HTTP ${response.status}`);
                } catch {
                    setSubmitError(`HTTP ${response.status}`);
                }
                return;
            }

            const result = await response.json();
            const targetFlowId = result.flowId || result.activeFlowId;
            flow.startFlow(targetFlowId);
        } catch (e: any) {
            setSubmitError(e.message || 'Network error');
        }
    };

    const isProcessing = flow.phase === 'processing' || flow.phase === 'rolling_back';
    const showSteps = flow.phase !== 'idle';
    const showRollback = flow.phase === 'rolling_back' || flow.phase === 'rolled_back'
        || (flow.phase === 'failed' && flow.rollbackSteps.length > 0);

    // Form phase (no active flow)
    if (flow.phase === 'idle') {
        return (
            <div className="convert-form">
                {/* Service name */}
                <div style={{ marginBottom: '12px' }}>
                    <label>Service</label>
                    <div style={{ fontSize: '14px', fontWeight: 500, padding: '8px 0' }}>{service.displayName}</div>
                </div>

                {/* Coach picker */}
                <div style={{ marginBottom: '12px' }}>
                    <label>Coach</label>
                    {coaches.length === 0 ? (
                        <div style={{ color: '#6c757d', fontSize: '13px' }}>No coaches available for this service.</div>
                    ) : (
                        <>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search coaches..."
                                value={coachSearch}
                                onChange={e => setCoachSearch(e.target.value)}
                                style={{ marginBottom: '6px' }}
                            />
                            <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', background: '#fff', marginBottom: '4px' }}>
                                {filteredCoaches.map(c => (
                                    <div
                                        key={c.personId}
                                        onClick={() => setSelectedCoachId(c.personId)}
                                        style={{
                                            padding: '6px 12px', cursor: 'pointer', fontSize: '14px',
                                            background: c.personId === selectedCoachId ? '#0d6efd' : 'transparent',
                                            color: c.personId === selectedCoachId ? '#fff' : '#212529',
                                        }}
                                    >
                                        {c.firstName} {c.lastName}{c.coachLevel ? ` (${c.coachLevel})` : ''}
                                    </div>
                                ))}
                            </div>
                            {selectedCoach && (
                                <div style={{ color: '#6c757d', fontSize: '12px' }}>
                                    Selected: {selectedCoach.firstName} {selectedCoach.lastName}{selectedCoach.coachLevel ? ` - ${selectedCoach.coachLevel}` : ''}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Location picker */}
                <div style={{ marginBottom: '12px' }}>
                    <label>Location</label>
                    {locations.length === 0 ? (
                        <div style={{ color: '#6c757d', fontSize: '13px' }}>No locations available for this service.</div>
                    ) : (
                        <>
                            {locations.length > 5 && (
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search locations..."
                                    value={locationSearch}
                                    onChange={e => setLocationSearch(e.target.value)}
                                    style={{ marginBottom: '6px' }}
                                />
                            )}
                            <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', background: '#fff', marginBottom: '4px' }}>
                                {filteredLocations.map(l => (
                                    <div
                                        key={l.locationId}
                                        onClick={() => setSelectedLocationId(l.locationId)}
                                        style={{
                                            padding: '6px 12px', cursor: 'pointer', fontSize: '14px',
                                            background: l.locationId === selectedLocationId ? '#0d6efd' : 'transparent',
                                            color: l.locationId === selectedLocationId ? '#fff' : '#212529',
                                        }}
                                    >
                                        {l.displayName}
                                    </div>
                                ))}
                            </div>
                            {selectedLocation && (
                                <div style={{ color: '#6c757d', fontSize: '12px' }}>
                                    Selected: {selectedLocation.displayName}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Date + Time */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                        <label>Date</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={bookingDate}
                            onChange={e => setBookingDate(e.target.value)}
                        />
                    </div>
                    <div style={{ flex: '0 0 100px' }}>
                        <label>Time</label>
                        <input
                            type="time"
                            className="form-control form-control-sm"
                            value={bookingTime}
                            onChange={e => setBookingTime(e.target.value)}
                        />
                    </div>
                </div>

                {/* Window info */}
                <div style={{ color: '#6c757d', fontSize: '12px', borderTop: '1px solid #e9ecef', paddingTop: '8px', marginBottom: '12px' }}>
                    Window: {new Date(voucher.windowStart).toLocaleDateString()} &mdash; {new Date(voucher.windowEnd).toLocaleDateString()}
                </div>

                {submitError && (
                    <div style={{ color: '#991b1b', fontSize: '13px', marginBottom: '8px' }}>{submitError}</div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={onBack}
                        style={{
                            flex: '0 0 auto', padding: '10px 16px', borderRadius: '6px',
                            border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                            fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                        }}
                    >
                        Back
                    </button>
                    <button
                        onClick={handleConvert}
                        disabled={!selectedCoachId || !selectedLocationId}
                        style={{
                            flex: 1, padding: '10px', borderRadius: '6px', border: 'none',
                            fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                            background: selectedCoachId && selectedLocationId ? '#059669' : '#93c5fd',
                            color: '#fff',
                        }}
                    >
                        Convert &amp; Book
                    </button>
                </div>
            </div>
        );
    }

    // Processing / progress phase
    return (
        <div className="voucher-body" style={{ paddingTop: '8px' }}>
            {/* Convert steps */}
            {showSteps && (
                <div style={{ marginBottom: '8px' }}>
                    {flow.steps.map(step => (
                        <div
                            key={step.key}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '3px 0',
                                color: step.status === 'pending' ? '#6c757d' : undefined,
                            }}
                        >
                            <StepIcon status={step.status} />
                            <div>
                                <span style={{
                                    color: step.status === 'pending' ? '#6c757d'
                                        : step.status === 'failed' ? '#dc3545' : undefined,
                                }}>
                                    {step.label}
                                </span>
                                {step.error && (
                                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{step.error}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Rollback section */}
            {showRollback && (
                <div style={{ borderTop: '2px solid #e9ecef', marginTop: '8px', paddingTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        {flow.phase === 'rolling_back' && (
                            <span className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px', color: '#f59e0b' }} />
                        )}
                        <span style={{ fontWeight: 600, color: '#f59e0b', fontSize: '13px' }}>
                            {flow.phase === 'rolling_back' ? 'Rolling back...'
                                : flow.phase === 'rolled_back' ? 'Rolled back'
                                : 'Rollback failed'}
                        </span>
                    </div>
                    {flow.rollbackSteps.map(step => (
                        <div
                            key={step.key}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '3px 0',
                                color: step.status === 'pending' ? '#6c757d' : undefined,
                            }}
                        >
                            <StepIcon status={step.status} />
                            <div>
                                <span style={{
                                    color: step.status === 'pending' ? '#6c757d'
                                        : step.status === 'failed' ? '#dc3545' : undefined,
                                }}>
                                    {step.label}
                                </span>
                                {step.error && (
                                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{step.error}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Success */}
            {flow.phase === 'done' && (
                <div className="converted-banner" style={{ margin: '-20px -24px', marginTop: '8px' }}>
                    <div className="check-icon">&#10003;</div>
                    <div className="converted-text">Converted Successfully</div>
                    <div className="converted-subtext">
                        Booking and order created for {voucher.personName || 'guest'}.
                    </div>
                </div>
            )}

            {/* Rolled back */}
            {flow.phase === 'rolled_back' && (
                <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '6px', margin: '8px 0' }}>
                    <strong style={{ color: '#92400e' }}>Conversion failed</strong>
                    <span style={{ color: '#92400e' }}> &mdash; all changes rolled back. Voucher is still Active.</span>
                </div>
            )}

            {/* Failed with rollback = rollback also failed */}
            {flow.phase === 'failed' && flow.rollbackSteps.length > 0 && (
                <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', margin: '8px 0' }}>
                    <strong style={{ color: '#991b1b' }}>Conversion failed</strong>
                    <span style={{ color: '#991b1b' }}> &mdash; automatic rollback also failed. Manual cleanup may be required.</span>
                </div>
            )}

            {/* Failed without rollback */}
            {flow.phase === 'failed' && flow.rollbackSteps.length === 0 && flow.error && (
                <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', margin: '8px 0' }}>
                    <strong style={{ color: '#991b1b' }}>Error:</strong>
                    <span style={{ color: '#991b1b' }}> {flow.error}</span>
                </div>
            )}

            {/* Close button for terminal states */}
            {!isProcessing && (
                <div style={{ textAlign: 'center', paddingTop: '12px' }}>
                    <button
                        onClick={flow.phase === 'done' ? onDone : onBack}
                        style={{
                            padding: '8px 24px', borderRadius: '6px', border: '1px solid #d1d5db',
                            background: '#fff', color: '#374151', fontSize: '14px', cursor: 'pointer',
                        }}
                    >
                        {flow.phase === 'done' ? 'Go Back' : 'Back'}
                    </button>
                </div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
                <div style={{ textAlign: 'center', paddingTop: '8px', color: '#6c757d', fontSize: '13px' }}>
                    <span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '12px', height: '12px' }} />
                    {flow.phase === 'rolling_back' ? 'Rolling back...' : 'Processing...'}
                </div>
            )}
        </div>
    );
};
